import React, { useEffect, useState } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP } from "../lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Banknote } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const BANCOS = [
  "BancoEstado", "Banco de Chile", "Banco Santander", "BCI", "Scotiabank",
  "Itaú", "BBVA", "Security", "Banco Internacional", "Falabella", "Ripley", "Consorcio", "Otro",
];

const EMPTY = {
  banco: "", tipo: "corriente", numero: "", titular: "", moneda: "CLP",
  saldo_inicial: 0, cuenta_contable_id: "", activa: true,
};

export default function CuentasBancarias() {
  const [list, setList] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cuentas-bancarias");
      setList(data);
      const { data: ct } = await api.get("/cuentas");
      setCuentas(ct.filter((c) => c.imputable && c.codigo.startsWith("1.1")));
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, saldo_inicial: parseFloat(form.saldo_inicial) || 0 };
      if (editing) await api.put(`/cuentas-bancarias/${editing.id}`, payload);
      else await api.post("/cuentas-bancarias", payload);
      toast.success(editing ? "Cuenta actualizada" : "Cuenta creada");
      setOpen(false); setEditing(null); setForm(EMPTY); load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      banco: c.banco, tipo: c.tipo, numero: c.numero, titular: c.titular || "",
      moneda: c.moneda, saldo_inicial: c.saldo_inicial, cuenta_contable_id: c.cuenta_contable_id || "",
      activa: c.activa !== false,
    });
    setOpen(true);
  };

  const del = async (id, banco) => {
    if (!window.confirm(`¿Eliminar cuenta ${banco}?`)) return;
    try { await api.delete(`/cuentas-bancarias/${id}`); toast.success("Eliminada"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  return (
    <Page>
      <PageHeader
        title="Cuentas bancarias"
        subtitle="Gestión de cuentas corrientes, vista y ahorro"
        testid="cb-header"
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
            <DialogTrigger asChild>
              <button data-testid="new-cb-button"
                className="inline-flex items-center gap-1.5 bg-[#1B4332] hover:bg-[#2D6A4F] text-white text-sm font-medium px-3 py-2 rounded-sm">
                <Plus className="h-4 w-4" /> Nueva cuenta
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? "Editar" : "Nueva"} cuenta bancaria</DialogTitle>
                <DialogDescription>Vincula una cuenta bancaria física a tu plan de cuentas contable.</DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-caps block mb-1">Banco</label>
                    <Select value={form.banco} onValueChange={(v) => setForm({ ...form, banco: v })}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                      <SelectContent>
                        {BANCOS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="label-caps block mb-1">Tipo</label>
                    <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="corriente">Cta. Corriente</SelectItem>
                        <SelectItem value="vista">Cta. Vista</SelectItem>
                        <SelectItem value="ahorro">Cta. Ahorro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-caps block mb-1">N° cuenta</label>
                    <input required value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm font-mono" />
                  </div>
                  <div>
                    <label className="label-caps block mb-1">Titular</label>
                    <input value={form.titular} onChange={(e) => setForm({ ...form, titular: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-caps block mb-1">Moneda</label>
                    <input value={form.moneda} onChange={(e) => setForm({ ...form, moneda: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm" />
                  </div>
                  <div>
                    <label className="label-caps block mb-1">Saldo inicial</label>
                    <input type="number" value={form.saldo_inicial} onChange={(e) => setForm({ ...form, saldo_inicial: e.target.value })}
                      className="w-full px-3 py-2 border border-[#E2DFD8] rounded-sm text-sm text-right font-mono" />
                  </div>
                </div>
                <div>
                  <label className="label-caps block mb-1">Cuenta contable asociada</label>
                  <Select value={form.cuenta_contable_id} onValueChange={(v) => setForm({ ...form, cuenta_contable_id: v })}>
                    <SelectTrigger><SelectValue placeholder="(Por defecto: 1.1.02 Banco)" /></SelectTrigger>
                    <SelectContent>
                      {cuentas.map((c) => <SelectItem key={c.id} value={c.id}>{c.codigo} · {c.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <button type="submit" className="bg-[#1B4332] hover:bg-[#2D6A4F] text-white text-sm font-medium px-4 py-2 rounded-sm">
                    {editing ? "Guardar" : "Crear"}
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-[#E2DFD8] rounded-md overflow-x-auto">
        {loading ? <div className="p-8 text-center text-[#59645E] text-sm">Cargando…</div>
          : list.length === 0 ? <div className="p-8 text-center text-[#59645E] text-sm">No hay cuentas bancarias. Crea una para empezar.</div>
          : (
          <table className="acc-table" data-testid="cb-table">
            <thead><tr>
              <th><Banknote className="inline h-3.5 w-3.5 mr-1" />Banco</th>
              <th>Tipo</th><th>Número</th><th>Titular</th><th>Moneda</th>
              <th className="num">Saldo inicial</th><th className="num">Saldo actual</th><th style={{ width: 80 }}></th>
            </tr></thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} data-testid={`cb-row-${c.id}`}>
                  <td className="font-medium">{c.banco}</td>
                  <td className="capitalize text-[12px]">{c.tipo}</td>
                  <td className="font-mono text-[12px]">{c.numero}</td>
                  <td>{c.titular || "—"}</td>
                  <td className="text-[12px]">{c.moneda}</td>
                  <td className="num">{fmtCLP(c.saldo_inicial)}</td>
                  <td className="num font-semibold">{fmtCLP(c.saldo_actual)}</td>
                  <td>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(c)} className="text-[#1B4332] hover:bg-[#1B4332]/10 p-1 rounded-sm"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => del(c.id, c.banco)} className="text-[#A94438] hover:bg-[#A94438]/10 p-1 rounded-sm"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Page>
  );
}
